import { Twitter, Send, Github, MessageCircle, ShieldCheck, BadgeCheck } from "lucide-react";

const CONTRACT = "0x246502B687a23fD4b37eD362263f6a52aF5C01C5";

// ── KYC BADGE TOGGLE ─────────────────────────────────────────────
// When you get KYC verified (e.g. Assure DeFi), set KYC_VERIFIED = true
// and paste your certificate/verification URL into KYC_URL. To remove,
// set KYC_VERIFIED = false.
const KYC_VERIFIED = false;
const KYC_PROVIDER = "Assure DeFi";
const KYC_URL = ""; // e.g. your Assure DeFi certificate link
// ─────────────────────────────────────────────────────────────────

const socials = [
  { icon: Twitter, label: "x", href: "https://x.com/AFTBRN63" },
  { icon: Send, label: "telegram", href: "https://t.me/AfterBurnReFi" },
  { icon: MessageCircle, label: "discord", href: "#" },
  { icon: Github, label: "github", href: "#" },
];

const verifyLinks = [
  { label: "BaseScan", href: `https://basescan.org/token/${CONTRACT}` },
  { label: "GoPlus Security", href: `https://gopluslabs.io/token-security/8453/${CONTRACT}` },
];
// Post-launch (after Uniswap liquidity exists) also add:
//   { label: "Token Sniffer", href: `https://tokensniffer.com/token/base/${CONTRACT}` },
//   { label: "Honeypot.is", href: `https://honeypot.is/base?address=${CONTRACT}` },
//   { label: "DexScreener", href: `https://dexscreener.com/base/${CONTRACT}` },

export default function Footer() {
  return (
    <footer className="border-t border-white/10 bg-card/40" data-testid="footer">
      <div className="px-6 md:px-12 max-w-7xl mx-auto py-16">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <img src="/logo-icon.png" alt="AfterBurn logo" className="w-9 h-9 object-contain" />
              <span className="font-display font-bold text-xl">
                After<span className="text-ember">Burn</span>
              </span>
            </div>
            <p className="mt-4 text-muted-foreground max-w-xs leading-relaxed">
              A regeneration token funding wildfire recovery — replanting forests and rebuilding the
              communities left behind.
            </p>
          </div>

          <div className="md:text-center">
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Explore</h4>
            <ul className="mt-4 space-y-3">
              {["Mission", "Tokenomics", "Markets", "Roadmap"].map((l) => (
                <li key={l}>
                  <a href={`#${l.toLowerCase()}`} className="text-foreground/80 hover:text-ember transition-colors">
                    {l}
                  </a>
                </li>
              ))}
              <li>
                <a href="/transparency" data-testid="footer-transparency-link" className="text-foreground/80 hover:text-ember transition-colors">
                  Transparency
                </a>
              </li>
              <li>
                <a href="/whitepaper" data-testid="footer-whitepaper-link" className="text-foreground/80 hover:text-ember transition-colors">
                  Whitepaper
                </a>
              </li>
            </ul>
          </div>

          <div className="md:text-right">
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Community</h4>
            <div className="mt-4 flex md:justify-end gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target={s.href !== "#" ? "_blank" : undefined}
                  rel={s.href !== "#" ? "noreferrer" : undefined}
                  data-testid={`social-${s.label}`}
                  className="w-11 h-11 border border-white/15 flex items-center justify-center hover:border-ember hover:text-ember transition-colors"
                >
                  <s.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10" data-testid="footer-verify">
          <div className="flex items-center gap-2 text-ember">
            <ShieldCheck className="w-4 h-4" />
            <h4 className="font-mono text-xs uppercase tracking-[0.2em]">Verify Us — Don't Trust, Verify</h4>
          </div>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
            $AFTBRN is a fixed-supply token on Base. Check our contract yourself on any of these
            independent security scanners:
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {KYC_VERIFIED && (
              <a
                href={KYC_URL || "#"}
                target={KYC_URL ? "_blank" : undefined}
                rel={KYC_URL ? "noreferrer" : undefined}
                data-testid="kyc-verified-badge"
                className="inline-flex items-center gap-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 px-4 py-2 text-sm font-semibold hover:bg-emerald-500/20 transition-colors"
              >
                <BadgeCheck className="w-4 h-4" />
                KYC Verified · {KYC_PROVIDER}
              </a>
            )}
            {verifyLinks.map((v) => (
              <a
                key={v.label}
                href={v.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`verify-${v.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-ember hover:text-ember transition-colors"
              >
                {v.label}
              </a>
            ))}
          </div>
          <p className="mt-4 font-mono text-[11px] text-muted-foreground break-all">
            Contract (Base): {CONTRACT}
          </p>
        </div>

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4 font-mono text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} AfterBurn ($AFTBRN) · after-burn.org · Built to regenerate.</span>
          <span>Crypto is volatile. This is not financial advice.</span>
        </div>
      </div>
    </footer>
  );
}
